import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { LookupService } from '@team/services/lookup.service';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

export const SP_SELECT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SpSelectComponent),
  multi: true
};

@Component({
  selector: 'sp-select',
  standalone: false,
  templateUrl: './sp-select.component.html',
  styleUrls: ['./sp-select.component.scss'],
  providers: [SP_SELECT_VALUE_ACCESSOR]

})
export class SpSelectComponent implements OnInit, SpSelectObjectInterface, OnChanges, ControlValueAccessor {
  @ViewChild('searchBoxInput', { static: true }) searchBoxInput: ElementRef;
  @ViewChild('api', { static: true }) ngSelect: NgSelectComponent;
  @Input() items: any = [];
  @Input() bindValue: string = 'id';
  @Input() bindLabel: string = 'label';
  @Input() size: string = 'default';
  @Input() status: string = 'default';
  @Input() addTagText: string = 'اضافة عنصر جديد';
  @Input() searchFieldLike: string = '';
  @Input() showBorder: boolean = false;
  @Input() multiple: boolean = false;
  @Input() checkBox: boolean = false;
  @Input() showTooltip: boolean = false;
  @Input() addTag: boolean = false;
  @Input() required: boolean = false;
  @Input() loading: boolean = false;
  @Input() typeahead = new Subject<any>();
  @Input() closeOnSelect: boolean = true;
  @Input() enableCount: boolean = false;
  @Input() searchBox: boolean = false;
  @Input() searchable: boolean = true;
  @Input() placeholder: string = 'Select an option';
  @Input() parentLabel: string = null;
  @Input() icon: string = null;
  @Input() warning: string = null;
  @Input() lookupType: string = null;
  @Input() lookupQuery: Record<string, unknown> = null;
  @Input() focus: boolean = false;
  @Input() focusAndOpen: boolean = false;
  @Output() onSelectChange = new EventEmitter<any>();
  @Output() parentIdClick = new EventEmitter<any>();
  @Output() onBlur = new EventEmitter<any>();
  @Input() itemsObjectName: string = 'items';
  @Input() groupBy = null;
  @Input() extraIntent = null;
  @Input() valueAsObject = false;
  @Input() loadingText = 'Loading ...';
  @Input() notFoundText = 'No Results Found';
  @Input() typeToSearchText = 'ابحث ...';
  @Input() disabled = null;
  @Input() selected = null;
  @Input() clearOnBackspace: boolean = true;
  @Input() clearable: boolean = true; // TODO: need to implement a close span in HTML when clearable is true
  @Input() multiple_text = 'options';
  isMobile: boolean = false;
  _data: Array<any>;
  count = 0;
  showOverlay = false;

  onChange: any = () => { };
  onTouched: any = () => { };

  get value() {
    return this.items;
  }

  set value(val) {
    this.items = val;
    this.onChange(val);
    this.onTouched(val);
  }

  ngOnInit() {
    if (this.lookupType) {
      this.initTypeahead();
    }
  }

  getLabelForSelectedItem(value) {
    let label = null;

    try {
      label = this.items.find(item => {
        const itemType = typeof item;
        let val = item;

        switch (itemType) {
          case 'object':
            val = item[this.bindValue];
            break;
        }

        return val === value;
      });
    } catch (e) { }

    try {
      return label[this.bindLabel];
    } catch (e) {
      // console.warn({
      //   message: 'Code SP-SELECT-001',
      //   ref: this.items
      // });
    }
  }

  emitOnBlur() {
    this.onBlur.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    // this clears the selection and counts when there is no list passed to items
    if (changes['items'] && (!changes['items'].currentValue || changes['items']?.currentValue?.length === 0)) {
      this.clearSelected();
    }
    if (changes['focus']?.currentValue) {
      setTimeout(() => {
        this.ngSelect.focus();
      });
    }
    if (changes['focusAndOpen']?.currentValue) {
      setTimeout(() => {
        this.ngSelect.open();
      });
    }
    let someHasNumber = false;

    try {
      someHasNumber = this.items.some(item => typeof item === 'number');
    } catch (e) { }

    if (this.items !== undefined && someHasNumber) {
      try {
        this.loadItems(this.items);
      } catch (e) {
        console.log(e, 'error loading items of object');
      }
    }
  }

  writeValue(v: any): void {
    if (v) {
      try {
        this.selected = v.map(item => item.id);
      } catch (e) { }
      try {
        this.count = this.selected.length;
      } catch (e) { }
    }

    this.selected = v ? v : null;
  }

  propagateChange(value) {
    value = value !== undefined ? value : null;

    if (value instanceof Event) {
      return;
    }
    this.hideOverlay();
    const valueType = typeof value;
    let programedValue = value;

    if (!this.valueAsObject) {
      switch (valueType) {
        case 'string':
          programedValue = value;
          break;
        case 'object':
          if (value !== null) {
            if ('length' in value) {
              programedValue = value.map(item => item.id);
            } else {
              programedValue = value[this.bindValue];
            }
          }
          break;
      }
      this.selected = programedValue;
    } else {
      if (programedValue) {
        if (typeof programedValue === 'object' && 'map' in programedValue) {
          this.selected = programedValue.map(item => item.id);
        }
      }
    }

    try {
      this.count = programedValue.length;
    } catch (e) {
      // has no count/ or it's not an array of type;
    }

    this.onSelectChange.emit(programedValue);
    this.onChange(programedValue);
    this.onTouched(programedValue);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /*
   * for binding data and labels if exist with ng-select
   */
  async loadItems(items) {
    this.items = await items.map(item => {
      if (typeof item !== 'object') {
        return {
          id: item,
          label: item
        };
      }
      return item;
    });
  }

  clearSelected() {
    this.count = 0;
    this.selected = null;
    this.onSelectChange.emit(null);
    this.onChange(null);
    this.onTouched(null);
  }

  constructor(private lookupService: LookupService, private cd: ChangeDetectorRef) { }

  initTypeahead() {
    this.typeahead
      .pipe(
        debounceTime(200),
        switchMap(term => {
          if (!term) {
            return [];
          }

          return this.lookupService[this.lookupType](term, this.lookupQuery).pipe(
            catchError(() => of({ items: [] })),
            map(rsp => {
              if (rsp[this.itemsObjectName]) {
                switch (this.lookupType) {
                  case 'lookupUsers':
                    return rsp[this.itemsObjectName].map(item => {
                      let first_name = '';
                      let last_name = '';
                      let mobile = '';

                      try {
                        first_name = item.name.first;
                      } catch (e) { }

                      try {
                        last_name = item.name.last;
                      } catch (e) { }

                      try {
                        mobile = item.mobile;
                      } catch (e) { }

                      return {
                        ...item,
                        name: `${mobile} ${first_name} ${last_name}`
                      };
                    });
                  case 'lookupCars':
                    const car = rsp[this.itemsObjectName];
                    let manufacture = '';
                    let model = '';
                    let year = '';

                    try {
                      manufacture = car.modelId.manufactureId.nameArabic;
                    } catch (e) { }
                    try {
                      model = car.modelId.nameArabic || '';
                    } catch (e) { }
                    try {
                      year = car.modelYear !== 0 ? car.modelYear : '';
                    } catch (e) { }

                    return [
                      {
                        label: `${manufacture} ${model} ${year}`,
                        id: car.modelId._id,
                        resp: rsp[this.itemsObjectName]
                      }
                    ];
                  case 'lookupAlgolia':
                    return rsp[this.itemsObjectName].map(hit => ({ label: hit.nameArabic, id: hit._id }));
                  default:
                    return rsp[this.itemsObjectName];
                }
              } else {
                return [];
              }
            })
          );
        })
      )
      .subscribe(
        items => {
          this.items = items;
          this.cd.markForCheck();
        },
        err => {
          this.items = [];
          this.cd.markForCheck();
        }
      );
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown($event: KeyboardEvent) {
    if ($event.key === 'Backspace' && this.clearOnBackspace) {
      this.clearSelected();
    }
  }
  clickedParent(e, item) {
    this.parentIdClick.emit(item);
    e.stopPropagation();
    return false;
  }

  changedSelect(e) {
    if (!e) {
      return;
    }
    const selectedElement = e.srcElement.selectedIndex;
    this.propagateChange(this.items[selectedElement]);
  }

  focusSearchBox(event) {
    const target = event.target as HTMLElement;
    if (target.tagName !== 'NG-SELECT') {
      return;
    }
    this.showOverlay = true;
    try {
      this.searchBoxInput.nativeElement.focus();
    } catch (e) { }
  }
  hideOverlay() {
    this.showOverlay = false;
  }
}

export interface SpSelectObjectInterface {
  items: any;
  bindValue?: string;
  bindLabel?: string;
  clearable?: boolean;
  clearOnBackspace?: boolean;
  size?: string;
  typeahead?: Subject<any>;
  loading?: boolean;
  addTag?: boolean;
  status?: string;
  multiple?: boolean;
  checkBox?: boolean;
  closeOnSelect?: boolean;
  enableCount?: boolean;
  searchBox?: boolean;
  placeholder?: string;
  icon: string;
  searchFieldLike?: string;
  showBorder?: boolean;
  addTagText?: string;
  multiple_text?: string;
}